import type { TrieKey } from '@mathflat/domain/Curriculum'
import type { SchoolTestType } from '@mathflat/domain/SchoolTest'
import { makeAutoObservable } from 'mobx'

import { MAX_REQUEST_NUMBER } from '@/constant/common'
import { schoolTestController } from '@/controller/schoolTest.controller'
import { SchoolTestDropdownManager } from '@/modules/schoolTest/services/SchoolTestDropdownManager.service'
import { curriculumCoreService } from '@/service/curriculumCore.service'

export type SchoolTestFilterOptionType = {
  searchType: 'REGION' | 'KEYWORD'
  year: string | 'ALL'
  schoolTestType: SchoolTestType | 'ALL'
  searchText: string
  schoolChecked: Set<string>
}
const DEFAULT_SCHOOL_TEST_FILTER: SchoolTestFilterOptionType = {
  searchType: 'REGION',
  year: '2023',
  schoolTestType: 'ALL',
  searchText: '',
  schoolChecked: new Set(),
}
export class SchoolTestFilterManager {
  // 필터(리모트스토리이지에있다면 리모트 스토리지값 아니면 디폴트값)
  private _schoolTestFilterOption = DEFAULT_SCHOOL_TEST_FILTER

  static 최대선택_학교수 = 10
  private _dropdownFilterManager = new SchoolTestDropdownManager()

  constructor() {
    makeAutoObservable(this)
  }
  get schoolTestFilterOption() {
    return this._schoolTestFilterOption
  }
  updateInitialSchoolTestFilter(filter?: Partial<SchoolTestFilterOptionType>) {
    this._schoolTestFilterOption = filter
      ? { ...this._schoolTestFilterOption, ...filter }
      : DEFAULT_SCHOOL_TEST_FILTER
  }
  // 필터 값 기본값으로 저장(view)
  resetAllFilter() {
    this.setTestTypeOptions('ALL')
    this.setYearOptions('ALL')
  }
  // 팝오버가 가지고 있는 초기 값
  resetSchoolTestFilterOption() {
    this._schoolTestFilterOption = DEFAULT_SCHOOL_TEST_FILTER
  }
  // 연도필터 관련
  get yearDropDownList() {
    return this._dropdownFilterManager.yearFilterOptions.dropdownList
  }
  get selectedYearId() {
    return this.yearDropDownList.find((option) => option.checked)?.id || 'ALL'
  }
  setYearOptions(value: string) {
    this._dropdownFilterManager.yearFilterOptions.updateCheckedNodes(value)
  }
  // 기말/중간기말 필터 관련
  get testTypeDropDownList() {
    return this._dropdownFilterManager.testTypeFilterOption.dropdownList
  }
  get selectedTestTypeId() {
    return this.testTypeDropDownList.find((option) => option.checked)?.id || 'ALL'
  }
  setTestTypeOptions(value: SchoolTestType | 'ALL') {
    this._dropdownFilterManager.testTypeFilterOption.updateCheckedNodes(value)
  }
  get schoolCodes() {
    return [...this.schoolTestFilterOption.schoolChecked.values()]
  }
  setSchoolCodes(schoolCodes: string[]) {
    this.schoolTestFilterOption.schoolChecked = new Set(schoolCodes)
  }
  get isDirty() {
    return this.selectedYearId !== 'ALL' || this.selectedTestTypeId !== 'ALL'
  }
  async fetchSchoolTests(trieKey: TrieKey) {
    // 선택 된 학년/학기 & 연도 & 중간기말 & 학교 코드를 조합해서 기출문제 fetch
    const curriculumInfo = curriculumCoreService.getItemByTrieKey(trieKey)
    if (!curriculumInfo) return
    const { gradeSubject, schoolType, semester } = curriculumInfo

    const { content: schoolTests } = await schoolTestController.fetchSchoolTestList({
      schoolCodes: this.schoolCodes,
      schoolType,
      grade: gradeSubject,
      semester,
      years: this.selectedYearId === 'ALL' ? undefined : [Number(this.selectedYearId)],
      schoolTestType:
        this.selectedTestTypeId === 'ALL' ? undefined : (this.selectedTestTypeId as SchoolTestType),
      size: MAX_REQUEST_NUMBER,
      page: 0,
      provideTypes: ['ALL', 'SIMILAR_ONLY'],
    })

    return schoolTests
  }
}
